'use client';

import { useId, type CSSProperties } from 'react';
import { cn } from '@/lib/utils';
import type { PaperKind } from '@/lib/piai/types';

const KIND_COLOR: Record<PaperKind, { base: string; deep: string; edge: string }> = {
  envelope: { base: '#c5d4e0', deep: '#9eb4c6', edge: '#879eb2' },
  house: { base: '#c5d0bc', deep: '#a3b59a', edge: '#8ea185' },
  car: { base: '#e8c9ae', deep: '#d4a888', edge: '#c49270' },
  gift: { base: '#d4c6de', deep: '#b8a6c6', edge: '#a290b2' },
  passport: { base: '#e6d9a8', deep: '#d4c484', edge: '#c2b06e' },
  document: { base: '#b7c5d4', deep: '#96a9bc', edge: '#8296ab' },
  calendar: { base: '#b8c8b0', deep: '#97ab8e', edge: '#83987a' },
};

export type PaperDetail = 'dot' | 'month' | 'week' | 'hero' | 'expand';

const SIZE: Record<PaperDetail, number> = {
  dot: 14,
  month: 52,
  week: 64,
  hero: 88,
  expand: 128,
};

interface PaperObjectProps {
  kind: PaperKind;
  detail?: PaperDetail;
  title?: string;
  className?: string;
  style?: CSSProperties;
  lifted?: boolean;
}

export function PaperObject({
  kind,
  detail = 'month',
  title,
  className,
  style,
  lifted = false,
}: PaperObjectProps) {
  const uid = useId().replace(/:/g, '');
  const sheenId = `sheen-${uid}`;
  const shadowId = `shadow-${uid}`;
  const colors = KIND_COLOR[kind];
  const size = SIZE[detail];
  const rich = detail === 'week' || detail === 'hero' || detail === 'expand';

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center relative transition-transform duration-300 ease-out',
        lifted && 'scale-110 -translate-y-1',
        className
      )}
      style={style}
      title={title}
      aria-hidden={!title}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 64 64"
        fill="none"
        className="overflow-visible"
        style={{
          filter: lifted
            ? 'drop-shadow(0 12px 20px rgba(44,42,38,0.22)) drop-shadow(0 2px 4px rgba(44,42,38,0.12))'
            : 'drop-shadow(0 6px 10px rgba(44,42,38,0.16)) drop-shadow(0 1px 2px rgba(44,42,38,0.1))',
        }}
      >
        {/* Cast shadow slab */}
        <ellipse
          cx="34"
          cy="56"
          rx="18"
          ry="4"
          fill="#2c2a26"
          opacity={lifted ? 0.18 : 0.1}
        />

        {/* Back paper layer — offset for depth */}
        <rect
          x="10"
          y="12"
          width="42"
          height="38"
          rx="7"
          fill={colors.deep}
          opacity="0.85"
        />
        {/* Mid layer */}
        <rect x="8" y="9" width="42" height="38" rx="7" fill={colors.edge} opacity="0.35" />
        {/* Front paper face */}
        <rect x="9" y="8" width="42" height="38" rx="7" fill={colors.base} />
        <rect
          x="9"
          y="8"
          width="42"
          height="38"
          rx="7"
          fill={`url(#${sheenId})`}
          opacity="0.55"
        />
        {/* Paper edge highlight */}
        <path
          d="M16 9.5 H44"
          stroke="#ffffff"
          strokeOpacity="0.55"
          strokeWidth="1.2"
          strokeLinecap="round"
        />

        {kind === 'envelope' && (
          <>
            <path
              d="M13 18 L30 30 L47 18"
              stroke="#2c2a26"
              strokeOpacity="0.28"
              strokeWidth="1.6"
              strokeLinejoin="round"
            />
            <path
              d="M13 18 H47 V38 H13 Z"
              fill="#fff8ef"
              fillOpacity="0.45"
            />
            <path
              d="M13 18 L30 30 L47 18"
              fill="#fff8ef"
              fillOpacity="0.65"
            />
            {rich && (
              <rect x="22" y="28" width="16" height="2" rx="1" fill="#2c2a26" fillOpacity="0.12" />
            )}
          </>
        )}

        {kind === 'house' && (
          <>
            <path d="M16 34 L30 20 L44 34 V44 H16 Z" fill="#fff8ef" fillOpacity="0.55" />
            <path
              d="M16 34 L30 20 L44 34"
              stroke="#2c2a26"
              strokeOpacity="0.2"
              strokeWidth="1.2"
            />
            <rect x="27" y="35" width="7" height="9" rx="1" fill="#2c2a26" fillOpacity="0.22" />
            {rich && (
              <>
                <rect x="19" y="30" width="5" height="5" rx="0.8" fill="#2c2a26" fillOpacity="0.12" />
                <rect x="36" y="30" width="5" height="5" rx="0.8" fill="#2c2a26" fillOpacity="0.12" />
              </>
            )}
          </>
        )}

        {kind === 'car' && (
          <>
            <path
              d="M14 36 H48 L44 27 H32 L28 22 H20 L16 27 Z"
              fill="#fff8ef"
              fillOpacity="0.6"
            />
            <path
              d="M20 22 L24 27 H31 L28 22 Z"
              fill="#2c2a26"
              fillOpacity="0.12"
            />
            <circle cx="22" cy="38" r="3.2" fill="#2c2a26" fillOpacity="0.28" />
            <circle cx="40" cy="38" r="3.2" fill="#2c2a26" fillOpacity="0.28" />
            <circle cx="22" cy="38" r="1.2" fill="#fff8ef" fillOpacity="0.5" />
            <circle cx="40" cy="38" r="1.2" fill="#fff8ef" fillOpacity="0.5" />
            {rich && (
              <rect x="34" y="29" width="6" height="3" rx="0.6" fill="#2c2a26" fillOpacity="0.1" />
            )}
          </>
        )}

        {kind === 'gift' && (
          <>
            <rect x="18" y="26" width="24" height="18" rx="3" fill="#fff8ef" fillOpacity="0.55" />
            <rect x="29" y="20" width="4" height="24" fill="#2c2a26" fillOpacity="0.16" />
            <rect x="18" y="31" width="24" height="4" fill="#2c2a26" fillOpacity="0.16" />
            <path
              d="M31 20 C26 14, 20 18, 31 26 C42 18, 36 14, 31 20"
              fill="#2c2a26"
              fillOpacity="0.14"
            />
          </>
        )}

        {kind === 'passport' && (
          <>
            <rect x="20" y="14" width="20" height="30" rx="3" fill="#fff8ef" fillOpacity="0.5" />
            <circle cx="30" cy="28" r="5" stroke="#2c2a26" strokeOpacity="0.28" strokeWidth="1.4" />
            <path d="M30 23 V33 M25 28 H35" stroke="#2c2a26" strokeOpacity="0.18" />
            {rich && (
              <>
                <rect x="24" y="36" width="12" height="1.5" rx="0.75" fill="#2c2a26" fillOpacity="0.15" />
                <rect x="25" y="39" width="10" height="1.5" rx="0.75" fill="#2c2a26" fillOpacity="0.1" />
              </>
            )}
          </>
        )}

        {kind === 'document' && (
          <>
            <rect x="18" y="14" width="24" height="30" rx="3" fill="#fff8ef" fillOpacity="0.55" />
            <path d="M34 14 V22 H42" fill="#fff8ef" fillOpacity="0.7" />
            <path d="M34 14 L42 22" stroke="#2c2a26" strokeOpacity="0.15" />
            <rect x="22" y="24" width="14" height="1.6" rx="0.8" fill="#2c2a26" fillOpacity="0.18" />
            <rect x="22" y="29" width="12" height="1.6" rx="0.8" fill="#2c2a26" fillOpacity="0.14" />
            <rect x="22" y="34" width="13" height="1.6" rx="0.8" fill="#2c2a26" fillOpacity="0.1" />
          </>
        )}

        {kind === 'calendar' && (
          <>
            <rect x="17" y="16" width="26" height="26" rx="3" fill="#fff8ef" fillOpacity="0.55" />
            <rect x="17" y="16" width="26" height="7" fill="#2c2a26" fillOpacity="0.14" />
            <circle cx="24" cy="32" r="1.8" fill="#2c2a26" fillOpacity="0.22" />
            <circle cx="30" cy="32" r="1.8" fill="#2c2a26" fillOpacity="0.18" />
            <circle cx="36" cy="32" r="1.8" fill="#2c2a26" fillOpacity="0.14" />
            {rich && (
              <circle cx="24" cy="38" r="1.8" fill="#2c2a26" fillOpacity="0.12" />
            )}
          </>
        )}

        <defs>
          <linearGradient id={sheenId} x1="9" y1="8" x2="51" y2="46">
            <stop stopColor="#ffffff" stopOpacity="0.7" />
            <stop offset="0.45" stopColor="#ffffff" stopOpacity="0.15" />
            <stop offset="1" stopColor="#2c2a26" stopOpacity="0.06" />
          </linearGradient>
          <filter id={shadowId}>
            <feDropShadow dx="0" dy="4" stdDeviation="3" floodOpacity="0.15" />
          </filter>
        </defs>
      </svg>
    </span>
  );
}
